import Fuse, { type IFuseOptions } from 'fuse.js';
import { getRegistryCache } from '@/route-data';
import type { FestivalRegistryEntry } from '@/festival-data';

export type SearchEntry = {
  type: 'festival' | 'journey' | 'event' | 'artist' | 'afterparty';
  id: string;
  name: string;
  shortName: string;
  location: string;
  region?: string;
  category: string;
  country: string;
  /** For artists/afterparties: which festival they belong to */
  festivalId?: string;
};

const FUSE_OPTIONS: IFuseOptions<SearchEntry> = {
  keys: [
    // shortName is the canonical short identifier (e.g. "Coachella") and
    // given highest weight so typo queries like "Cochella" rank against
    // the compact name rather than a substring in a longer name.
    { name: 'shortName', weight: 0.5 },
    { name: 'name', weight: 0.3 },
    { name: 'location', weight: 0.1 },
    { name: 'region', weight: 0.05 },
    { name: 'category', weight: 0.05 },
  ],
  threshold: 0.4,
  // Small distance (20) anchors the match near position 0, penalising
  // entries where the matching substring is buried in the middle of the
  // field (e.g. "chella" in "Afrochella" at pos 4 loses to "Coachella"
  // where the whole word matches from the start).
  location: 0,
  distance: 20,
  minMatchCharLength: 2,
  includeScore: true,
};

/** Convert a FestivalRegistryEntry to a SearchEntry. */
export function festivalToSearchEntry(f: Pick<FestivalRegistryEntry, 'id' | 'name' | 'shortName' | 'location' | 'region' | 'category' | 'country'>): SearchEntry {
  return {
    type: 'festival',
    id: f.id,
    name: f.name,
    shortName: f.shortName,
    location: f.location,
    region: f.region,
    category: f.category,
    country: f.country,
  };
}

/** Lazy singleton — shared across all components to avoid duplicate memory. */
let _cachedIndex: Fuse<SearchEntry> | null = null;
/** Track what festival entries the cache was built with (cheap invalidation check). */
let _cachedFestivalLen = -1;
let _cachedFestivalFirstId = '';

let _eventEntries: SearchEntry[] = [];
let _artistEntries: SearchEntry[] = [];
let _afterpartyEntries: SearchEntry[] = [];

/**
 * Get the shared Fuse.js search index (singleton, built on first call).
 * Pass festivalEntries from useFestivalRegistry() so the index reflects
 * API-sourced festival data instead of the static bundle import.
 * If festivalEntries is omitted or empty, festivals are excluded from results.
 */
export function getSearchIndex(festivalEntries?: SearchEntry[]): Fuse<SearchEntry> {
  const entries = festivalEntries ?? [];
  const newLen = entries.length;
  const newFirstId = entries[0]?.id ?? '';

  // Invalidate cache when festival data changes (array length or first id)
  if (
    _cachedIndex === null ||
    newLen !== _cachedFestivalLen ||
    newFirstId !== _cachedFestivalFirstId
  ) {
    _cachedFestivalLen = newLen;
    _cachedFestivalFirstId = newFirstId;
    _cachedIndex = buildSearchIndex(entries);
  }

  return _cachedIndex;
}

/** Add event entries to the index (call after local events are loaded) */
export function addEventEntries(events: Array<{ id: string; name: string; city?: string; country?: string; category: string }>) {
  // Invalidate cached index so next getSearchIndex() call rebuilds with events
  _cachedIndex = null;
  _cachedFestivalLen = -1;
  _cachedFestivalFirstId = '';
  _eventEntries = events.map(e => ({
    type: 'event' as const,
    id: e.id,
    name: e.name,
    shortName: e.name,
    location: e.city || '',
    category: e.category,
    country: e.country || '',
  }));
}

/** Add artist entries to the index (call after festival data is loaded) */
export function addArtistEntries(artists: Array<{ id: string; name: string; genre: string; festivalId: string; festivalName: string; venue?: string }>) {
  _cachedIndex = null;
  _cachedFestivalLen = -1;
  _cachedFestivalFirstId = '';
  _artistEntries = artists.map(a => ({
    type: 'artist' as const,
    id: a.id,
    name: a.name,
    shortName: a.name,
    location: a.venue || a.festivalName,
    category: a.genre,
    country: '',
    festivalId: a.festivalId,
  }));
}

/** Add afterparty entries to the index (call after festival data is loaded) */
export function addAfterpartyEntries(parties: Array<{ id: string; name: string; venue: string; genre: string; festivalId: string; headliners?: string[] }>) {
  _cachedIndex = null;
  _cachedFestivalLen = -1;
  _cachedFestivalFirstId = '';
  _afterpartyEntries = parties.map(p => ({
    type: 'afterparty' as const,
    id: p.id,
    name: p.name,
    shortName: p.headliners?.join(', ') || p.name,
    location: p.venue,
    category: p.genre,
    country: '',
    festivalId: p.festivalId,
  }));
}

/**
 * Build a fresh Fuse.js search index covering festivals (from caller), journeys, events, artists, and afterparties.
 * Prefer getSearchIndex(festivalEntries) for production use. This factory is kept for tests.
 */
export function buildSearchIndex(festivalEntries?: SearchEntry[]): Fuse<SearchEntry> {
  const entries: SearchEntry[] = [
    ...(festivalEntries ?? []),
    ...getRegistryCache().map(r => ({
      type: 'journey' as const,
      id: r.id,
      name: r.name,
      shortName: r.shortName,
      location: r.location,
      region: r.region,
      category: r.category,
      country: r.country,
    })),
    ..._eventEntries,
    ..._artistEntries,
    ..._afterpartyEntries,
  ];
  return new Fuse(entries, FUSE_OPTIONS);
}
